"use client";

import { RAINBOW_KIT_PROJECT_ID } from "@/constants/wallet.constants";
import type { WagmiConfigType } from "@/providers/crypto.provider";
import { getChainsByConfigType } from "@/utils/wagmi/select-chains-by-config-type.util";
import { getDefaultConfig } from "@rainbow-me/rainbowkit";
import {
  mainnet,
  arbitrum,
  optimism,
  base,
  sepolia,
  optimismSepolia,
  arbitrumSepolia,
  baseSepolia,
} from "wagmi/chains";
import { createStorage, http } from "wagmi";
import { blessnetSepolia, blessnetMainnet } from "@/config/bless-chain";

/**
 * Uses the getDefautlConfig function from RainbowKit to create a configuration object
 *
 * Creates a configuration object  based on the specified Wagmi configuration type.
 *
 * @param configType - The type of Wagmi configuration to use. Determines the chains and storage key.
 * @returns A configuration object for RainbowKit with the specified settings.
 *
 * The configuration includes:
 * - The application name ("Blessnet").
 * - A project ID for RainbowKit integration.
 * - The chains determined by the provided configuration type.
 * - Server-side rendering (SSR) support enabled.
 * - A custom storage mechanism with a key based on the configuration type.
 */
export const createConfigWithRainbowKit = (configType: WagmiConfigType) => {
  const chains = getChainsByConfigType(configType);
  const isClient = typeof window !== "undefined";
  return getDefaultConfig({
    appName: "Blessnet",
    projectId: RAINBOW_KIT_PROJECT_ID,
    chains,
    transports: {
      // Use public RPCs to avoid API rate limits
      [blessnetMainnet.id]: http(blessnetMainnet.rpcUrls.default.http[0]),
      [mainnet.id]: http("https://ethereum-rpc.publicnode.com"),
      [arbitrum.id]: http("https://arbitrum-one-rpc.publicnode.com"),
      [optimism.id]: http("https://optimism-rpc.publicnode.com"),
      [base.id]: http("https://base-rpc.publicnode.com"),
      [blessnetSepolia.id]: http(blessnetSepolia.rpcUrls.default.http[0]),
      [sepolia.id]: http("https://ethereum-sepolia-rpc.publicnode.com"),
      [arbitrumSepolia.id]: http("https://arbitrum-sepolia-rpc.publicnode.com"),
      [optimismSepolia.id]: http("https://optimism-sepolia-rpc.publicnode.com"),
      [baseSepolia.id]: http("https://base-sepolia-rpc.publicnode.com"),
    },
    ssr: true,
    // Local storage is accessed only on the client side
    storage: isClient
      ? createStorage({
          storage: window.localStorage,
          key: configType,
        })
      : undefined,
  });
};
